import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import medaiLogo from '@/assets/medai-cite-logo-64.webp';
import { Button } from '@/components/ui/button';
import { BookMarked, LogOut, LogIn, Moon, Sun, Crown, Menu } from 'lucide-react';
import { useTheme } from 'next-themes';
import { LanguageSelector } from './LanguageSelector';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';

export const Header = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();
  const { theme, setTheme } = useTheme();
  const { plan } = useSubscription();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <div 
          className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/')}
        >
          <img 
            src={medaiLogo}
            alt="MedAI Cite Logo" 
            className="h-8 w-8 object-contain"
            width="32"
            height="32"
          />
          <h1 className="text-xl md:text-2xl font-bold">
            {t.appTitle}
          </h1>
        </div>
        
        {/* Hamburger Menu - Visible on all devices */}
        <div className="flex gap-2 items-center">
          <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Abrir menú</span>
              </Button>
            </SheetTrigger>
            <SheetContent className="w-[300px] sm:w-[400px]">
              <SheetHeader>
                <SheetTitle>{t.appTitle}</SheetTitle>
              </SheetHeader>
              
              <div className="flex flex-col gap-4 mt-6">
                {/* Language Selector */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Idioma</span>
                  <LanguageSelector currentLanguage={language} onLanguageChange={setLanguage} />
                </div>

                <Separator />

                {/* Theme Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Tema</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                  >
                    <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                    <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                    <span className="sr-only">Toggle theme</span>
                  </Button>
                </div>

                <Separator />

                {/* User Actions */}
                {user ? (
                  <>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => {
                        navigate('/saved-searches');
                        setIsMenuOpen(false);
                      }}
                    >
                      <BookMarked className="mr-2 h-4 w-4" />
                      {t.mySearches}
                    </Button>
                    
                    <Button 
                      variant={plan === 'free' ? 'default' : 'outline'}
                      className="w-full justify-start"
                      onClick={() => {
                        navigate('/pricing');
                        setIsMenuOpen(false);
                      }}
                    >
                      <Crown className="mr-2 h-4 w-4" />
                      {plan === 'free' ? t.upgradePlan : `Plan ${plan.charAt(0).toUpperCase() + plan.slice(1)}`}
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => {
                        handleSignOut();
                        setIsMenuOpen(false);
                      }}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      {t.logout}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => {
                        navigate('/pricing');
                        setIsMenuOpen(false);
                      }}
                    >
                      <Crown className="mr-2 h-4 w-4" />
                      {t.plans}
                    </Button>
                    
                    <Button 
                      variant="default" 
                      className="w-full justify-start"
                      onClick={() => {
                        navigate('/auth');
                        setIsMenuOpen(false);
                      }}
                    >
                      <LogIn className="mr-2 h-4 w-4" />
                      {t.login}
                    </Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
